import { useMemo, useState } from 'react'
import { Scatter } from 'react-chartjs-2'
import { Card, CardContent, Typography, Box, FormControl, InputLabel, Select, MenuItem, Chip, Grid, Dialog, DialogTitle, DialogContent, IconButton } from '@mui/material'
import { alpha } from '@mui/material/styles'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import CloseIcon from '@mui/icons-material/Close'
import {
  Chart as ChartJS,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend
} from 'chart.js'

ChartJS.register(LinearScale, PointElement, LineElement, Tooltip, Legend)

// Calculate Pearson correlation coefficient
function correlationCoefficient(x, y) {
  if (x.length !== y.length || x.length < 2) return 0

  const n = x.length
  const sumX = x.reduce((a, b) => a + b, 0)
  const sumY = y.reduce((a, b) => a + b, 0)
  const sumXY = x.reduce((sum, xi, i) => sum + xi * y[i], 0)
  const sumX2 = x.reduce((sum, xi) => sum + xi * xi, 0)
  const sumY2 = y.reduce((sum, yi) => sum + yi * yi, 0)

  const numerator = n * sumXY - sumX * sumY
  const denominator = Math.sqrt((n * sumX2 - sumX * sumX) * (n * sumY2 - sumY * sumY))

  return denominator === 0 ? 0 : numerator / denominator
}

// Linear regression
function linearRegression(x, y) {
  if (x.length !== y.length || x.length < 2) return { slope: 0, intercept: 0 }

  const n = x.length
  const sumX = x.reduce((a, b) => a + b, 0)
  const sumY = y.reduce((a, b) => a + b, 0)
  const sumXY = x.reduce((sum, xi, i) => sum + xi * y[i], 0)
  const sumX2 = x.reduce((sum, xi) => sum + xi * xi, 0)

  const slope = (n * sumXY - sumX * sumY) / (n * sumX2 - sumX * sumX)
  const intercept = (sumY - slope * sumX) / n

  return { slope, intercept }
}

const VARIABLES = [
  { key: 'equipment_age', label: 'Equipment Age (years)', accessor: d => d.equipment_age },
  { key: 'capacity_tons', label: 'Capacity (tons)', accessor: d => d.capacity_tons },
  { key: 'efficiency_pct', label: 'Efficiency Improvement (%)', accessor: d => d.efficiency_pct },
  { key: 'kw_per_ton_before', label: 'kW/Ton Before', accessor: d => d.kw_per_ton_before },
  { key: 'kw_per_ton_after', label: 'kW/Ton After', accessor: d => d.kw_per_ton_after },
  { key: 'annual_savings', label: 'Annual Savings ($)', accessor: d => d.annual_savings },
  { key: 'kw_reduction', label: 'kW/Ton Reduction (%)', accessor: d => d.kw_per_ton_before && d.kw_per_ton_after ? ((d.kw_per_ton_before - d.kw_per_ton_after) / d.kw_per_ton_before * 100) : null }
]

function CorrelationAnalysis({ data, title = 'Correlation Analysis' }) {
  const [xVar, setXVar] = useState('equipment_age')
  const [yVar, setYVar] = useState('efficiency_pct')
  const [explainOpen, setExplainOpen] = useState(true)

  const analysis = useMemo(() => {
    const xConfig = VARIABLES.find(v => v.key === xVar)
    const yConfig = VARIABLES.find(v => v.key === yVar)

    if (!xConfig || !yConfig) return null

    // Extract valid data points
    const points = data
      .map(d => ({
        x: xConfig.accessor(d),
        y: yConfig.accessor(d),
        label: d.equipment_type || 'Unknown'
      }))
      .filter(p => p.x != null && p.y != null && !isNaN(p.x) && !isNaN(p.y))

    if (points.length < 2) return null

    const xValues = points.map(p => p.x)
    const yValues = points.map(p => p.y)

    const r = correlationCoefficient(xValues, yValues)
    const regression = linearRegression(xValues, yValues)

    // Generate trend line points
    const minX = Math.min(...xValues)
    const maxX = Math.max(...xValues)
    const trendLine = [
      { x: minX, y: regression.slope * minX + regression.intercept },
      { x: maxX, y: regression.slope * maxX + regression.intercept }
    ]

    return {
      points,
      r,
      rSquared: r * r,
      regression,
      trendLine,
      n: points.length,
      xLabel: xConfig.label,
      yLabel: yConfig.label
    }
  }, [data, xVar, yVar])

  if (!analysis) {
    return (
      <Card sx={{ height: '100%' }}>
        <CardContent>
          <Typography variant="subtitle1" fontWeight={600}>{title}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Insufficient data for correlation analysis.
          </Typography>
        </CardContent>
      </Card>
    )
  }

  const chartData = {
    datasets: [
      {
        label: 'Data Points',
        data: analysis.points,
        backgroundColor: 'rgba(160, 206, 78, 0.6)',
        borderColor: 'rgba(160, 206, 78, 1)',
        pointRadius: 5,
        pointHoverRadius: 7
      },
      {
        label: 'Trend Line',
        data: analysis.trendLine,
        type: 'line',
        borderColor: 'rgba(100, 100, 100, 0.8)',
        borderWidth: 2,
        borderDash: [5, 5],
        pointRadius: 0,
        fill: false
      }
    ]
  }

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      datalabels: {
        display: false
      },
      legend: {
        display: false
      },
      tooltip: {
        callbacks: {
          label: (context) => {
            if (context.datasetIndex === 0) {
              const point = analysis.points[context.dataIndex]
              return [
                `${analysis.xLabel}: ${context.parsed.x.toFixed(2)}`,
                `${analysis.yLabel}: ${context.parsed.y.toFixed(2)}`,
                `Type: ${point.label}`
              ]
            }
            return ''
          }
        }
      }
    },
    scales: {
      x: {
        title: {
          display: true,
          text: analysis.xLabel
        }
      },
      y: {
        title: {
          display: true,
          text: analysis.yLabel
        }
      }
    }
  }

  // Correlation strength interpretation
  const getCorrelationStrength = (r) => {
    const absR = Math.abs(r)
    if (absR >= 0.7) return { label: 'Strong', color: 'success' }
    if (absR >= 0.4) return { label: 'Moderate', color: 'warning' }
    if (absR >= 0.2) return { label: 'Weak', color: 'info' }
    return { label: 'Very Weak', color: 'default' }
  }

  const strength = getCorrelationStrength(analysis.r)

  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
          <Typography variant="subtitle1" fontWeight={600}>
            {title}
          </Typography>
          <Chip label={`n=${analysis.n}`} size="small" />
          <Chip
            label={`r = ${analysis.r.toFixed(3)}`}
            size="small"
            color={strength.color}
          />
          <Chip
            label={`R² = ${(analysis.rSquared * 100).toFixed(1)}%`}
            size="small"
            variant="outlined"
          />
        </Box>

        {/* Variable Selectors */}
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={6}>
            <FormControl fullWidth size="small">
              <InputLabel>X Axis</InputLabel>
              <Select
                value={xVar}
                label="X Axis"
                onChange={(e) => setXVar(e.target.value)}
              >
                {VARIABLES.map(v => (
                  <MenuItem key={v.key} value={v.key}>{v.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6}>
            <FormControl fullWidth size="small">
              <InputLabel>Y Axis</InputLabel>
              <Select
                value={yVar}
                label="Y Axis"
                onChange={(e) => setYVar(e.target.value)}
              >
                {VARIABLES.map(v => (
                  <MenuItem key={v.key} value={v.key}>{v.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        {/* Interpretation - Clickable for explanation */}
        <Box
          onClick={() => setExplainOpen(true)}
          sx={{
            mb: 2,
            p: 1.5,
            bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
            borderRadius: 1,
            borderLeft: 3,
            borderColor: 'primary.main',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            transition: 'background-color 0.2s',
            '&:hover': {
              bgcolor: (theme) => alpha(theme.palette.primary.main, 0.12)
            }
          }}
        >
          <Typography variant="caption" color="text.primary">
            <strong>{strength.label} {analysis.r >= 0 ? 'positive' : 'negative'} correlation</strong>
            {' '}— {analysis.rSquared * 100 > 20
              ? `${analysis.xLabel} explains ${(analysis.rSquared * 100).toFixed(0)}% of variance in ${analysis.yLabel}`
              : 'Variables are largely independent'}
          </Typography>
          <InfoOutlinedIcon sx={{ fontSize: 18, color: 'text.secondary', ml: 1, flexShrink: 0 }} />
        </Box>

        {/* Explanation Modal */}
        <Dialog
          open={explainOpen}
          onClose={() => setExplainOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
            Understanding Correlation
            <IconButton onClick={() => setExplainOpen(false)} size="small">
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers>
            <Typography variant="h6" gutterBottom color="primary">
              {strength.label} {analysis.r >= 0 ? 'Positive' : 'Negative'} Correlation (r = {analysis.r.toFixed(3)})
            </Typography>

            <Typography variant="body2" paragraph>
              <strong>What this means:</strong> When one variable goes {analysis.r >= 0 ? 'up' : 'down'},
              the other tends to go {analysis.r >= 0 ? 'up too' : 'down'} — but only {strength.label.toLowerCase()}.
            </Typography>

            <Typography variant="body2" paragraph>
              <strong>The r value ({analysis.r.toFixed(3)}):</strong>
            </Typography>
            <Box component="ul" sx={{ pl: 2, mt: 0, mb: 2 }}>
              <Typography component="li" variant="body2">r = 1.0 means perfect connection</Typography>
              <Typography component="li" variant="body2">r = 0.0 means no connection (random)</Typography>
              <Typography component="li" variant="body2">r = {analysis.r.toFixed(3)} means {strength.label.toLowerCase()} connection</Typography>
            </Box>

            <Typography variant="body2" paragraph>
              <strong>R² = {(analysis.rSquared * 100).toFixed(1)}%</strong> — This tells us how much of one
              variable's behavior can be explained by the other. Here, {analysis.xLabel} explains
              only {(analysis.rSquared * 100).toFixed(1)}% of the variation in {analysis.yLabel}.
            </Typography>

            <Typography variant="body2" color="text.secondary" sx={{ mt: 2, fontStyle: 'italic' }}>
              {analysis.rSquared * 100 < 20
                ? `Bottom line: These variables are largely independent. Knowing ${analysis.xLabel} doesn't help predict ${analysis.yLabel}.`
                : `Bottom line: There's a ${strength.label.toLowerCase()} relationship between these variables.`
              }
            </Typography>

            {/* Special insight for equipment age correlation */}
            {(xVar === 'equipment_age' || yVar === 'equipment_age') && analysis.rSquared * 100 < 20 && (
              <Box sx={{ mt: 2, p: 1.5, bgcolor: (theme) => alpha(theme.palette.success.main, 0.1), borderRadius: 1 }}>
                <Typography variant="body2" color="success.main">
                  <strong>What this means for PermaFrost NMR:</strong> As a chemical treatment, it delivers consistent results regardless of equipment age. Whether equipment is 3 years old or 25 years old, the efficiency improvements are similar. The treatment works every time.
                </Typography>
              </Box>
            )}
          </DialogContent>
        </Dialog>

        {/* Chart */}
        <Box sx={{ height: 220 }}>
          <Scatter data={chartData} options={chartOptions} />
        </Box>
      </CardContent>
    </Card>
  )
}

export default CorrelationAnalysis
